import { useState } from 'react'
import { Screen } from '../components/Layout'
import { Button, Card, ErrorBanner, Field, Spinner, inputClass } from '../components/ui'
import {
  useCategories,
  useCreateCategory,
  useDeleteCategory,
  useUpdateCategory,
} from '../lib/queries'

/**
 * Category and sub-category CRUD (requirement 9).
 *
 * Two things this screen has to be honest about, because both surprise people:
 *
 * - Renaming a built-in category is an *overlay*, not a copy. Every past expense
 *   keeps its id and immediately shows the new label, which is what users expect
 *   and the reason renames are safe.
 * - "Delete" archives. The label has to keep existing or last year's report grows
 *   a blank slice, so the button says 停用 and offers to move the expenses to
 *   another category first.
 */
export function CategoriesPage() {
  const [showArchived, setShowArchived] = useState(false)
  const taxonomy = useCategories(showArchived)
  const create = useCreateCategory()
  const update = useUpdateCategory()
  const remove = useDeleteCategory()

  const [newMain, setNewMain] = useState('')
  const [addingSubTo, setAddingSubTo] = useState<string | undefined>()
  const [newSub, setNewSub] = useState('')
  const [renaming, setRenaming] = useState<string | undefined>()
  const [renameTo, setRenameTo] = useState('')
  const [archiving, setArchiving] = useState<string | undefined>()
  const [reassignTo, setReassignTo] = useState('')

  const busy = create.isPending || update.isPending || remove.isPending
  const error = create.error ?? update.error ?? remove.error

  function submitRename(id: string): void {
    const label = renameTo.trim()
    if (!label) return
    update.mutate({ id, body: { label } }, { onSuccess: () => setRenaming(undefined) })
  }

  return (
    <Screen
      title="分類"
      action={
        <button
          type="button"
          className="text-sm text-accent"
          onClick={() => setShowArchived(!showArchived)}
        >
          {showArchived ? '隱藏停用' : '顯示停用'}
        </button>
      }
    >
      <div className="space-y-4">
        <ErrorBanner error={error} />

        <Card className="space-y-3 p-4">
          <Field label="新增主分類">
            <input
              className={inputClass}
              value={newMain}
              onChange={(e) => setNewMain(e.target.value)}
              placeholder="例如：寵物"
            />
          </Field>
          <Button
            className="w-full"
            disabled={newMain.trim() === '' || busy}
            onClick={() =>
              create.mutate({ label: newMain.trim() }, { onSuccess: () => setNewMain('') })
            }
          >
            新增
          </Button>
        </Card>

        {taxonomy.isLoading ? <Spinner /> : null}
        {taxonomy.error ? (
          <ErrorBanner error={taxonomy.error} onRetry={() => void taxonomy.refetch()} />
        ) : null}

        {taxonomy.data?.map((main) => (
          <Card key={main.id} className={`p-4 ${main.archived ? 'opacity-50' : ''}`}>
            <div className="flex items-center gap-2">
              <span
                aria-hidden
                className="size-3 shrink-0 rounded-full"
                style={{ backgroundColor: main.color }}
              />
              {renaming === main.id ? (
                <input
                  className={`${inputClass} py-2`}
                  value={renameTo}
                  autoFocus
                  onChange={(e) => setRenameTo(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitRename(main.id)
                    if (e.key === 'Escape') setRenaming(undefined)
                  }}
                  onBlur={() => submitRename(main.id)}
                />
              ) : (
                <>
                  <span className="flex-1 truncate">{main.label}</span>
                  {main.archived ? <Tag>已停用</Tag> : null}
                  {main.isCustom ? <Tag>自訂</Tag> : null}
                </>
              )}
            </div>

            {main.subs.length > 0 ? (
              <ul className="mt-3 flex flex-wrap gap-2">
                {main.subs.map((sub) => (
                  <li key={sub.id}>
                    {renaming === `${main.id}#${sub.id}` ? (
                      <input
                        className={`${inputClass} w-36 py-1.5 text-sm`}
                        value={renameTo}
                        autoFocus
                        onChange={(e) => setRenameTo(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') submitRename(`${main.id}#${sub.id}`)
                          if (e.key === 'Escape') setRenaming(undefined)
                        }}
                        onBlur={() => submitRename(`${main.id}#${sub.id}`)}
                      />
                    ) : (
                      <span
                        className={`inline-flex items-center gap-1 rounded-full bg-ink px-3 py-1.5 text-sm ring-1 ring-ink-line ${
                          sub.archived ? 'text-muted line-through' : ''
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            setRenaming(`${main.id}#${sub.id}`)
                            setRenameTo(sub.label)
                          }}
                        >
                          {sub.label}
                        </button>
                        {!sub.archived ? (
                          <button
                            type="button"
                            aria-label={`停用 ${sub.label}`}
                            className="text-muted"
                            onClick={() => setArchiving(`${main.id}#${sub.id}`)}
                          >
                            ×
                          </button>
                        ) : null}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            ) : null}

            {addingSubTo === main.id ? (
              <div className="mt-3 flex gap-2">
                <input
                  className={`${inputClass} py-2`}
                  autoFocus
                  value={newSub}
                  onChange={(e) => setNewSub(e.target.value)}
                  placeholder="子分類名稱"
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setAddingSubTo(undefined)
                  }}
                />
                <Button
                  className="px-3 py-2 text-sm"
                  disabled={newSub.trim() === '' || busy}
                  onClick={() =>
                    create.mutate(
                      { label: newSub.trim(), parentId: main.id },
                      {
                        onSuccess: () => {
                          setNewSub('')
                          setAddingSubTo(undefined)
                        },
                      },
                    )
                  }
                >
                  加入
                </Button>
              </div>
            ) : null}

            {archiving === main.id || archiving?.startsWith(`${main.id}#`) ? (
              <div className="mt-3 rounded-xl bg-ink p-3 text-sm ring-1 ring-ink-line">
                <p>停用後不會再出現在記帳畫面，已記錄的支出會保留。</p>
                <Field label="把現有支出改到（選填）">
                  <select
                    className={`${inputClass} py-2`}
                    value={reassignTo}
                    onChange={(e) => setReassignTo(e.target.value)}
                  >
                    <option value="">保持原分類</option>
                    {taxonomy.data
                      ?.filter((m) => !m.archived && m.id !== archiving)
                      .map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.label}
                        </option>
                      ))}
                  </select>
                </Field>
                <div className="mt-3 flex gap-2">
                  <Button
                    variant="danger"
                    className="flex-1 py-2 text-sm"
                    disabled={busy}
                    onClick={() =>
                      remove.mutate(
                        {
                          id: archiving,
                          ...(reassignTo ? { reassignTo } : {}),
                        },
                        {
                          onSuccess: () => {
                            setArchiving(undefined)
                            setReassignTo('')
                          },
                        },
                      )
                    }
                  >
                    停用
                  </Button>
                  <Button
                    variant="secondary"
                    className="flex-1 py-2 text-sm"
                    onClick={() => {
                      setArchiving(undefined)
                      setReassignTo('')
                    }}
                  >
                    取消
                  </Button>
                </div>
              </div>
            ) : null}

            <div className="mt-3 flex gap-4 text-sm text-muted">
              <button
                type="button"
                onClick={() => {
                  setRenaming(main.id)
                  setRenameTo(main.label)
                }}
              >
                改名稱
              </button>
              {!main.archived ? (
                <button type="button" onClick={() => setAddingSubTo(main.id)}>
                  加子分類
                </button>
              ) : null}
              {main.archived ? (
                <button
                  type="button"
                  className="text-accent"
                  onClick={() => update.mutate({ id: main.id, body: { archived: false } })}
                >
                  重新啟用
                </button>
              ) : main.id === 'other' ? (
                // `other` is where reassignment and unmatched imports land, so the
                // API refuses to archive it. Not offering the button beats showing
                // one that always errors.
                <span>預設分類</span>
              ) : (
                <button type="button" onClick={() => setArchiving(main.id)}>
                  停用
                </button>
              )}
            </div>
          </Card>
        ))}
      </div>
    </Screen>
  )
}

function Tag({ children }: { children: string }) {
  return <span className="rounded bg-ink px-1.5 py-0.5 text-xs text-muted">{children}</span>
}
